import { Injectable, signal, Signal, inject } from '@angular/core';
import { ChatMessage, ChatRole, IMiniAgentCard, AppAgentTask, IAgentCard, ISimpleTask, SimpleAgentTask } from '../models/agent.models';
import { CONVERSATION_AI_TOKEN, AgentCardsAbstractService } from '../models/conversation-ai.class';
import { extractJsonFromResponse } from '../models/agent.utils';
import { TOAST_ALERTS_TOKEN, ToastAlertsAbstractService } from '@dataclouder/ngx-core';
import { ConversationService } from './conversation.service'; // Import ConversationService

import { ContextType } from '../models/agent.models'; // Assuming you add the enum here or adjust path

@Injectable({
  providedIn: 'root',
})
export class EvaluationService {
  // Services
  private agentCardService = inject<AgentCardsAbstractService>(CONVERSATION_AI_TOKEN);
  private toastService = inject<ToastAlertsAbstractService>(TOAST_ALERTS_TOKEN);
  private conversationService = inject(ConversationService); // Inject ConversationService
  // Signals
  private scoreSignal = signal<number>(0);
  private evaluationResultSignal = signal<any>(null);

  // Get score as a signal
  getScore(): Signal<number> {
    return this.scoreSignal;
  }

  setScore(score: number): void {
    this.scoreSignal.set(score);
  }
  // Get evaluation result as a signal
  getEvaluationResult(): Signal<any> {
    return this.evaluationResultSignal;
  }

  // Evaluate conversation
  async evaluateConversation(messages: ChatMessage[], evaluator: IMiniAgentCard): Promise<any> {
    // Filter conversation to only include user and assistant messages
    const conversationMessages = messages.filter((message) => [ChatRole.User, ChatRole.Assistant].includes(message.role));

    // Check if there are any user messages to evaluate
    const userMessages = conversationMessages.filter((message) => message.role === ChatRole.User);

    if (userMessages.length <= 0) {
      console.log('No messages to evaluate', conversationMessages);
      return;
    }
    const lastUserMessage = userMessages[userMessages.length - 1]; // Get the last user message for update

    // Format conversation for evaluation
    const conversationMessagesString = conversationMessages.map((message) => `${message.role}: ${message.content}`).join('\n');

    // Create evaluation prompt
    const instructions = `
Please replay to this task:
    ${evaluator.task}
This is the conversation history:
    ${conversationMessagesString}
and give the response in next JSON format.
    ${evaluator.expectedResponseType}
    `;

    // TODO: evaluations gemini flashcard

    // Send evaluation request
    const evaluationMessages: ChatMessage[] = [{ content: instructions, role: ChatRole.User }];
    // Not sure what strategy use, if works types should be definied in definitions, generarally response will be score, and feedback.
    const response = await this.agentCardService.callChatCompletion({ messages: evaluationMessages });

    // Extract JSON from response
    const jsonData = extractJsonFromResponse(response.content);
    this.evaluationResultSignal.set(jsonData);

    this.toastService.info({
      title: 'Score ' + jsonData.score,
      subtitle: `Added ${jsonData.feedback} points`,
    });

    // Update score if available
    if (jsonData.score) {
      if (jsonData.score <= 3) {
        this.updateScore(jsonData.score * 10);
      }
    }

    // Update the specific user message with the evaluation result
    if (lastUserMessage?.messageId && jsonData) {
      this.conversationService.updateMessage(lastUserMessage.messageId, {
        evaluation: jsonData, // Add evaluation data to the message
      });
    }

    return jsonData;
  }

  // Helper function to get context messages based on type
  private getContextMessages(messages: ChatMessage[], contextType: ContextType): ChatMessage[] {
    const conversationMessages = messages.filter((message) => [ChatRole.User, ChatRole.Assistant].includes(message.role));

    if (conversationMessages.length === 0) {
      return [];
    }

    switch (contextType) {
      case ContextType.LastAssistantMessage: {
        const lastAssistant = conversationMessages
          .slice()
          .reverse()
          .find((m) => m.role === ChatRole.Assistant);
        return lastAssistant ? [lastAssistant] : [];
      }
      case ContextType.LastUserMessage: {
        const lastUser = conversationMessages
          .slice()
          .reverse()
          .find((m) => m.role === ChatRole.User);
        return lastUser ? [lastUser] : [];
      }
      case ContextType.LastDialog: {
        const lastUser = conversationMessages
          .slice()
          .reverse()
          .find((m) => m.role === ChatRole.User);
        const lastAssistant = conversationMessages
          .slice()
          .reverse()
          .find((m) => m.role === ChatRole.Assistant);
        const dialog: ChatMessage[] = [];
        if (lastAssistant) dialog.push(lastAssistant);
        if (lastUser) dialog.push(lastUser);
        // Sort by timestamp if available, otherwise keep order [Assistant, User] if both exist
        // Assuming messageId or a timestamp property exists and is comparable
        // dialog.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0)); // Example sorting
        return dialog;
      }
      case ContextType.Last2Dialogs: {
        const users = conversationMessages.filter((m) => m.role === ChatRole.User);
        const assistants = conversationMessages.filter((m) => m.role === ChatRole.Assistant);
        const lastTwoUsers = users.slice(-2);
        const lastTwoAssistants = assistants.slice(-2);
        const dialogs = [...lastTwoAssistants, ...lastTwoUsers];
        // Sort by timestamp if available
        // dialogs.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0)); // Example sorting
        return dialogs;
      }
      case ContextType.AllConversation:
      default:
        return conversationMessages;
    }
  }

  /**
   * Evaluates a conversation context based on a specific task and attaches the result to a designated message.
   * @param agentTask The task definition for the evaluation.
   * @param messageToUpdate The specific ChatMessage object (user or assistant) to attach the evaluation results to.
   * @param contextType Determines which part of the conversation history to send as context (default: AllConversation).
   * @returns The JSON result of the evaluation.
   */
  async evaluateWithTask(
    agentTask: SimpleAgentTask,
    messageToUpdate: ChatMessage, // Message to attach the evaluation to
    contextType: ContextType = ContextType.AllConversation,
  ): Promise<any> {
    // Fetch the current conversation messages from the service
    const messages = this.conversationService.getMessagesSignal()();
    const requestMessages: ChatMessage[] = [];

    const systemPrompt = agentTask.systemPrompt || 'You are a helpful assistant.'; // Default fallback
    requestMessages.push({ role: ChatRole.System, content: systemPrompt });

    let taskContent = agentTask.task;
    if (agentTask.expectedResponseType) {
      taskContent += `\n\nPlease provide the response strictly in the following JSON format:\n${agentTask.expectedResponseType}`;
    }

    if (contextType === ContextType.CurrentMessageContent) {
      taskContent += `\n\nHere is text: ${messageToUpdate.content}`;
    } else {
      // Get context messages based on the specified type
      const contextMessages = this.getContextMessages(messages, contextType);

      if (contextMessages.length > 0) {
        const conversationHistoryString = contextMessages
          .map((message) => `${message.role}: ${message.content}`) // Format messages
          .join('\n');
        taskContent += `\n\nHere is the relevant conversation history:\n${conversationHistoryString}`;
      }
    }

    // No longer need to find the last user message here, it's passed in as messageToUpdate

    requestMessages.push({ role: ChatRole.User, content: taskContent });

    try {
      const response = await this.agentCardService.callChatCompletion({ messages: requestMessages, model: agentTask.model });
      let jsonData = extractJsonFromResponse(response.content);
      if (jsonData === null) {
        jsonData = { score: 1, feedback: 'Good job!' };
      }
      this.evaluationResultSignal.set(jsonData);

      // Update score if available
      if (jsonData.score) {
        // Adjust the multiplier or condition if needed for task-based evaluation
        if (jsonData.score <= 3) {
          this.updateScore(jsonData.score * 10);
        }
      }

      // Update the specific message provided by the caller with the result
      if (messageToUpdate?.messageId && jsonData) {
        this.conversationService.updateMessage(messageToUpdate.messageId, { evaluation: jsonData });
      }

      return jsonData;
    } catch (error: any) {
      console.error('Error during evaluateWithTask:', error);
      this.toastService.error({
        title: 'Task Evaluation Failed',
        subtitle: error?.message || 'An unexpected error occurred while evaluating the task.',
      });
      // Depending on requirements, re-throw the error or return a specific error object
      throw error;
    }
  }

  private updateScore(additionalScore: number): void {
    this.scoreSignal.update((currentScore) => {
      const newScore = currentScore + additionalScore;
      return newScore > 100 ? 100 : newScore;
    });
  }

  resetScore(): void {
    this.scoreSignal.set(0);
  }
}
